import { Injectable } from '@angular/core';
import * as L from 'leaflet';
import { BehaviorSubject } from 'rxjs';
import { MapLayerCategoryEnum } from '../models/map-layer-category.enum';
import { MapLayerDefinition } from '../models/map-layer-definition.model';
import { MapLayerGroup } from '../models/map-layer-group.model';
import { MapLayerPresetType } from '../models/map-layer-preset.type';

/**
 * Central registry responsible for tracking all map layers, their categories,
 * visibility state and base-layer selection.
 *
 * This service keeps the Leaflet map and the custom Angular layers panel synchronized.
 */
@Injectable()
export class MapLayerRegistryService {
    /** Internal dictionary of registered layers keyed by technical id. */
    private readonly registry = new Map<string, MapLayerDefinition>();

    /** Reactive stream of the current layer snapshot for UI consumption. */
    private readonly layersSubject = new BehaviorSubject<MapLayerDefinition[]>([]);

    /** Public observable used by the custom layers panel. */
    readonly layers$ = this.layersSubject.asObservable();

    /**
     * Registers a new layer definition in the internal registry.
     *
     * @param definition Layer definition to register.
     */
    registerLayer(definition: MapLayerDefinition): void {
        this.registry.set(definition.id, definition);
        this.emit();
    }

    /**
     * Registers multiple layers at once.
     *
     * @param definitions Layers to register.
     */
    registerLayers(definitions: MapLayerDefinition[]): void {
        definitions.forEach((definition) => this.registry.set(definition.id, definition));
        this.emit();
    }

    /**
     * Removes a registered layer by id.
     *
     * @param layerId Technical id of the layer to remove.
     */
    unregisterLayer(layerId: string): void {
        this.registry.delete(layerId);
        this.emit();
    }

    /**
     * Returns all layers currently registered.
     *
     * @returns Array with a detached snapshot of the registry values.
     */
    getAllLayers(): MapLayerDefinition[] {
        return Array.from(this.registry.values()).sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    /**
     * Returns a single layer by id.
     *
     * @param layerId Technical id.
     * @returns Layer definition if found.
     */
    getLayer(layerId: string): MapLayerDefinition | undefined {
        return this.registry.get(layerId);
    }

    /**
     * Returns the group definitions used by the custom layers panel.
     *
     * @returns Ordered layer groups.
     */
    getGroupedLayers(): MapLayerGroup[] {
        const allLayers = this.getAllLayers();

        const groups: MapLayerGroup[] = [
        {
            category: MapLayerCategoryEnum.TRAJECTORY,
            label: 'Trajectory',
            icon: 'git-branch-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.TRAJECTORY),
        },
        {
            category: MapLayerCategoryEnum.BASE,
            label: 'Base map',
            icon: 'map-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.BASE),
        },
        {
            category: MapLayerCategoryEnum.COORDINATION_AREAS,
            label: 'Coordination areas',
            icon: 'shield-checkmark-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.COORDINATION_AREAS),
        },
        {
            category: MapLayerCategoryEnum.INFRASTRUCTURE,
            label: 'Infrastructure',
            icon: 'business-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.INFRASTRUCTURE),
        },
        {
            category: MapLayerCategoryEnum.OPERATIONS,
            label: 'Operations',
            icon: 'navigate-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.OPERATIONS),
        },
        {
            category: MapLayerCategoryEnum.POLICE_AREAS,
            label: 'Police areas',
            icon: 'shield-half-outline',
            expanded: true,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.POLICE_AREAS),
        },
        {
            category: MapLayerCategoryEnum.NEAR_SEARCH,
            label: 'Near search',
            icon: 'search-outline',
            expanded: false,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.NEAR_SEARCH),
        },
        {
            category: MapLayerCategoryEnum.RELAY,
            label: 'Relay',
            icon: 'radio-outline',
            expanded: false,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.RELAY),
        },
        {
            category: MapLayerCategoryEnum.DEBUG,
            label: 'Debug',
            icon: 'bug-outline',
            expanded: false,
            layers: allLayers.filter((layer) => layer.category === MapLayerCategoryEnum.DEBUG),
        },
        ];

        return groups.filter((group) => group.layers.length > 0);
    }

    /**
     * Updates visibility of a registered overlay layer.
     *
     * @param layerId Layer id.
     * @param visible Whether the layer should be visible.
     */
    setOverlayVisibility(layerId: string, visible: boolean): void {
        const layer = this.registry.get(layerId);
        if (!layer || layer.type !== 'overlay') {
        return;
        }

        layer.visible = visible;
        this.emit();
    }

    /**
     * Marks one base layer as selected and hides the rest.
     *
     * @param layerId Selected base layer id.
     */
    setBaseLayer(layerId: string): void {
        this.registry.forEach((layer) => {
        if (layer.type === 'base') {
            layer.visible = layer.id === layerId;
        }
        });

        this.emit();
    }

    /**
     * Applies a coarse visibility preset to registered layers.
     *
     * @param preset Preset to apply.
     */
    applyPreset(preset: MapLayerPresetType): void {
        this.registry.forEach((layer) => {
        if (layer.type === 'base') {
            return;
        }

        switch (preset) {
            case 'infrastructure':
            layer.visible = layer.category === MapLayerCategoryEnum.INFRASTRUCTURE
                || layer.category === MapLayerCategoryEnum.COORDINATION_AREAS;
            break;
            case 'operations':
            layer.visible = layer.category === MapLayerCategoryEnum.OPERATIONS;
            break;
            case 'pso':
            case 'police':
            layer.visible = layer.category === MapLayerCategoryEnum.POLICE_AREAS;
            break;
            case 'relay':
            layer.visible = layer.category === MapLayerCategoryEnum.RELAY;
            break;
            case 'debug':
            layer.visible = layer.category === MapLayerCategoryEnum.DEBUG;
            break;
            case 'default':
            default:
            layer.visible = !layer.debugOnly;
            break;
        }
        });

        this.emit();
    }

    /**
     * Shows all overlay layers.
     */
    showAllOverlays(): void {
        this.registry.forEach((layer) => {
        if (layer.type === 'overlay' && !layer.disabled) {
            layer.visible = true;
        }
        });

        this.emit();
    }

    /**
     * Hides all overlay layers.
     */
    hideAllOverlays(): void {
        this.registry.forEach((layer) => {
        if (layer.type === 'overlay') {
            layer.visible = false;
        }
        });

        this.emit();
    }

    /**
     * Clears the registry completely.
     */
    clear(): void {
        this.registry.clear();
        this.emit();
    }

    /**
     * Applies the current registry snapshot to a live Leaflet map.
     *
     * Base layers are kept mutually exclusive.
     * Overlay layers are independently added or removed.
     *
     * @param map Leaflet map instance.
     */
    syncToMap(map: L.Map): void {
        if (!map) {
        return;
        }

        const allLayers = this.getAllLayers();
        const baseLayers = allLayers.filter((layer) => layer.type === 'base');
        const overlayLayers = allLayers.filter((layer) => layer.type === 'overlay');

        baseLayers.forEach((layerDef) => {
        const hasLayer = map.hasLayer(layerDef.layer);
        if (layerDef.visible && !hasLayer) {
            layerDef.layer.addTo(map);
        } else if (!layerDef.visible && hasLayer) {
            map.removeLayer(layerDef.layer);
        }
        });

        overlayLayers.forEach((layerDef) => {
        const hasLayer = map.hasLayer(layerDef.layer);
        if (layerDef.visible && !hasLayer) {
            layerDef.layer.addTo(map);
        } else if (!layerDef.visible && hasLayer) {
            map.removeLayer(layerDef.layer);
        }
        });
    }

    /**
     * Emits a new immutable snapshot to subscribers.
     */
    private emit(): void {
        this.layersSubject.next(this.getAllLayers().map((layer) => ({ ...layer })));
    }
}